import React, { useContext, useCallback } from 'react';
import classnames from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { Checkbox } from 'ui-components/components/checkbox';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';

import { CountryDropdown } from '../../../../components/customs-declarations/components/origin-country-dropdown/origin-country-dropdown';
import Global from '../../../../store/global-selectors';
import { HarmonizationSuggestion } from '../../../harmonization/harmonization-suggestion';
import { useIsHarmonizationSuggestionEnabled } from '../../../harmonization/use-is-harmonization-suggestion-enabled';
import { ProductDetailsContext } from '../../context';
import { messages } from '../../messages';
import style from '../../style.module.less';
import { MID_CODE_MAX_LENGTH, CPSC_CERTIFICATE_FIELD_MAX_LENGTH } from '../../validation/schema';
import { useEnableCpscCertificateFields } from '../../../../feature-flags/hooks/use-enable-cpsc-certificate-fields';

export const ProductDetailsCustomsDeclarations = () => {
  const {
    productId,
    userHasPermission,
    noAutoCustomsDeclaration,
    setNoAutoCustoms,
    customsDescription,
    setCustomsDescription,
    customsDeclaredValue,
    setCustomsDeclaredValue,
    customsHarmonizationCode,
    setCustomsHarmonizationCode,
    customsMIDCode,
    setCustomsMIDCode,
    cpscCertificateVersionId,
    setCpscCertificateVersionId,
    cpscCertifierId,
    setCpscCertifierId,
    customsOriginCountry,
    setCustomsOriginCountry,
    validationErrors,
  } = useContext(ProductDetailsContext);
  const intl = useIntl();
  const currencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const isHarmonizationSuggestionEnabled = useIsHarmonizationSuggestionEnabled();
  const isCpscEnabled = useEnableCpscCertificateFields();

  const onCustomsValueChange = useCallback(
    (value) => {
      setCustomsDeclaredValue(value);
    },
    [setCustomsDeclaredValue],
  );

  const onMIDCodeChange = useCallback(
    (event) => {
      setCustomsMIDCode(event.target.value);
    },
    [setCustomsMIDCode],
  );

  const onCpscCertificateVersionIdChange = useCallback(
    (event) => {
      setCpscCertificateVersionId(event.target.value);
    },
    [setCpscCertificateVersionId],
  );

  const onCpscCertifierIdChange = useCallback(
    (event) => {
      setCpscCertifierId(event.target.value);
    },
    [setCpscCertifierId],
  );

  return (
    <>
      <div className={classnames(style['shipping-defaults-header-wrapper'], style.dropped)}>
        <div className={style['shipping-defaults-header-text']}>
          <FormattedMessage {...messages.customsTitle} />
        </div>
      </div>
      <div className={style['defaults-info-message']}>
        <FormattedMessage {...messages.customsDefaultsSubHeader} />
      </div>
      <div className={style['customs-options']}>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.customsDeclarationsCheckbox} />
          </div>
          <Checkbox
            checked={noAutoCustomsDeclaration}
            className={style['no-customs-options']}
            disabled={!userHasPermission}
            label={intl.formatMessage(messages.noAutoCustoms)}
            onChange={setNoAutoCustoms}
          />
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.description} />
          </div>
          <div className={style['paired-input']}>
            <Input
              className={style['input-wrapper']}
              data-testid='customs-description'
              disabled={!userHasPermission}
              onChange={setCustomsDescription}
              value={customsDescription}
            />
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.declaredValue} />
          </div>
          <div className={style['paired-input']}>
            <CurrencyInput
              className={style['currency-input']}
              currencyCode={currencyCode}
              data-testid='customs-declared-value'
              disabled={!userHasPermission}
              onChange={onCustomsValueChange}
              value={customsDeclaredValue}
            />
            {validationErrors?.customsDeclaredValue?.message ? (
              <InlineMessage type={INLINE_MESSAGE_TYPES.error}>{validationErrors.customsDeclaredValue.message}</InlineMessage>
            ) : null}
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.customsHarmonization} />
          </div>
          <div className={style['harmonization-input-row']}>
            <div className={style['paired-input']}>
              <Input
                className={style['input-wrapper']}
                data-testid='customs-harmonization'
                disabled={!userHasPermission}
                onChange={setCustomsHarmonizationCode}
                value={customsHarmonizationCode}
              />
            </div>
            {isHarmonizationSuggestionEnabled ? (
              <HarmonizationSuggestion onCodeSelected={setCustomsHarmonizationCode} productId={productId} />
            ) : null}
          </div>
        </div>
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <Text element='div' textType={TextType.Label} value={messages.customsMIDCode} />
          </div>
          <div className={style['paired-input']}>
            <Input
              className={style['input-wrapper']}
              data-testid='customs-mid-code'
              disabled={!userHasPermission}
              inlineMessage={validationErrors?.customsMIDCode?.message}
              inlineMessageType={validationErrors?.customsMIDCode?.message ? INLINE_MESSAGE_TYPES.error : undefined}
              maxLength={MID_CODE_MAX_LENGTH}
              onChange={onMIDCodeChange}
              value={customsMIDCode ?? ''}
              version={version2}
            />
          </div>
        </div>
        {isCpscEnabled ? (
          <>
            <div className={style['paired-options']}>
              <div className={style['leftside-label']}>
                <Text element='div' textType={TextType.Label} value={messages.cpscCertificateVersionId} />
              </div>
              <div className={style['paired-input']}>
                <Input
                  className={style['input-wrapper']}
                  data-testid='cpsc-certificate-version-id'
                  disabled={!userHasPermission}
                  maxLength={CPSC_CERTIFICATE_FIELD_MAX_LENGTH}
                  onChange={onCpscCertificateVersionIdChange}
                  value={cpscCertificateVersionId ?? ''}
                  version={version2}
                />
              </div>
            </div>
            <div className={style['paired-options']}>
              <div className={style['leftside-label']}>
                <Text element='div' textType={TextType.Label} value={messages.cpscCertifierId} />
              </div>
              <div className={style['paired-input']}>
                <Input
                  className={style['input-wrapper']}
                  data-testid='cpsc-certifier-id'
                  disabled={!userHasPermission}
                  maxLength={CPSC_CERTIFICATE_FIELD_MAX_LENGTH}
                  onChange={onCpscCertifierIdChange}
                  value={cpscCertifierId ?? ''}
                  version={version2}
                />
              </div>
            </div>
          </>
        ) : null}
        <div className={style['paired-options']}>
          <div className={style['leftside-label']}>
            <FormattedMessage {...messages.customsOriginCountry} />
          </div>
          <div className={style['origin-country']}>
            <CountryDropdown
              onChange={setCustomsOriginCountry}
              pickerClassName={style['dropdown-picker']}
              selectedCountryCode={customsOriginCountry}
            />
          </div>
        </div>
      </div>
    </>
  );
};
